import { App as CapacitorApp } from '@capacitor/app'
import { CapacitorUpdater } from '@capgo/capacitor-updater'
import nacl from 'tweetnacl'
import { appVersionApi } from '@/api/appVersion'
import { alert, confirm, toast } from './feedback'
import { http } from './http'
import { getPlatform, isNativeApp, openExternalUrl } from './native'
import { getStorage, setStorage } from './storage'

interface LatestBundleResponse {
  updateAvailable: boolean
  bundleVersion: string
  minNativeVersion?: string
  url?: string
  sha256?: string
  signature?: string
  size?: number
  mandatory?: boolean
  releaseNotes?: string
  publishedAt?: string
}

const CHANNEL = import.meta.env.VITE_CAPACITOR_CHANNEL || 'production'
const STORAGE_KEY = 'appWebBundleVersion'
const DEVICE_ID_KEY = 'appWebBundleDeviceId'
let updateInFlight: Promise<void> | null = null

export async function initializeOta() {
  if (!isNativeApp()) {
    return
  }

  await CapacitorUpdater.notifyAppReady().catch(() => undefined)
  CapacitorApp.addListener('appStateChange', ({ isActive }) => {
    if (isActive) {
      void checkAndInstallWebBundle(false).catch(() => undefined)
    }
  })
  window.setTimeout(() => {
    void checkNativeAppVersion().finally(() => checkAndInstallWebBundle(false)).catch(() => undefined)
  }, 1000)
}

export async function checkNativeAppVersion() {
  if (!isNativeApp())
    return false
  const [current, latest] = await Promise.all([
    CapacitorApp.getInfo(),
    appVersionApi.latest(),
  ])
  const currentBuild = Number(current.build || 0)
  if (!latest || Number(latest.versionCode || 0) <= currentBuild)
    return false

  const size = latest.fileSize ? ` (${latest.fileSize})` : ''
  const notes = latest.description || parseChangelog(latest.changelog)
  const accepted = await confirm({
    title: '发现新版本',
    message: `发现新版本 ${latest.versionName || latest.versionCode}${size}${notes ? `\n\n${notes}` : ''}\n\n是否前往下载？`,
    confirmButtonText: '立即下载',
    cancelButtonText: '稍后再说',
  })
  if (accepted && latest.downloadUrl)
    await openExternalUrl(latest.downloadUrl)
  return true
}

function parseChangelog(value?: string) {
  if (!value)
    return ''
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed.join('\n') : String(parsed || '')
  }
  catch {
    return value
  }
}

export async function checkAndInstallWebBundle(interactive = true) {
  if (updateInFlight) {
    return updateInFlight
  }
  updateInFlight = performUpdateCheck(interactive).finally(() => {
    updateInFlight = null
  })
  return updateInFlight
}

async function performUpdateCheck(interactive: boolean) {
  if (!isNativeApp()) {
    if (interactive) {
      toast('当前为浏览器预览，无需热更新')
    }
    return
  }

  const [current, pending] = await Promise.all([
    CapacitorUpdater.current().catch(() => null),
    CapacitorUpdater.getNextBundle().catch(() => null),
  ])
  const currentBundleVersion = pending?.version || current?.bundle?.version || getStorage(STORAGE_KEY) || 'builtin'
  const nativeInfo = await CapacitorApp.getInfo()
  const deviceId = getOrCreateDeviceId()
  const latest = await http.get<LatestBundleResponse>(
    `/public/app-web-bundles/latest?platform=${getPlatform()}&nativeVersion=${encodeURIComponent(nativeInfo.version)}&bundleVersion=${encodeURIComponent(currentBundleVersion)}&channel=${encodeURIComponent(CHANNEL)}&deviceId=${encodeURIComponent(deviceId)}`,
    { ignoreAuth: true, toastError: interactive },
  )

  if (!latest.updateAvailable || !latest.url || !latest.bundleVersion) {
    if (interactive) {
      toast('当前已是最新资源')
    }
    return
  }

  if (latest.minNativeVersion && compareVersion(nativeInfo.version, latest.minNativeVersion) < 0) {
    await alert(`当前 App 版本为 ${nativeInfo.version}，该资源包要求 App ${latest.minNativeVersion} 或更高版本。请前往应用商店更新整包。`)
    return
  }

  const shouldInstall = latest.mandatory || !interactive || await confirm({
    title: '发现资源更新',
    message: latest.releaseNotes || `发现新版资源 ${latest.bundleVersion}，是否立即更新？`,
    confirmButtonText: '立即更新',
    cancelButtonText: '稍后',
  })
  if (!shouldInstall) {
    return
  }

  verifyBundleSignature(latest)

  const bundle = await CapacitorUpdater.download({
    url: latest.url,
    version: latest.bundleVersion,
    checksum: latest.sha256,
  })
  setStorage(STORAGE_KEY, latest.bundleVersion)

  if (latest.mandatory) {
    await alert('资源更新已安装，应用将重新加载。')
    await CapacitorUpdater.set({ id: bundle.id })
    return
  }

  await CapacitorUpdater.next({ id: bundle.id })
  toast('资源更新将在下次启动生效', 'success')
}

function verifyBundleSignature(bundle: LatestBundleResponse) {
  if (!bundle.sha256 || !/^[a-f0-9]{64}$/iu.test(bundle.sha256)) {
    throw new Error('资源包缺少有效的 SHA256，已拒绝安装')
  }
  if (!bundle.signature) {
    throw new Error('资源包缺少 Ed25519 签名，已拒绝安装')
  }
  const configuredKey = import.meta.env.VITE_APP_WEB_BUNDLE_SIGNING_PUBLIC_KEY?.trim()
  if (!configuredKey) {
    throw new Error('App 未配置 OTA 签名公钥，已拒绝安装')
  }
  const publicKey = decodePublicKey(configuredKey)
  const signature = decodeBase64(bundle.signature)
  const digest = Uint8Array.from(bundle.sha256.match(/.{2}/gu) || [], value => Number.parseInt(value, 16))
  if (publicKey.length !== nacl.sign.publicKeyLength || signature.length !== nacl.sign.signatureLength) {
    throw new Error('资源包签名格式无效，已拒绝安装')
  }
  if (!nacl.sign.detached.verify(digest, signature, publicKey)) {
    throw new Error('资源包签名校验失败，已拒绝安装')
  }
}

function decodePublicKey(value: string) {
  const normalized = value.replace(/\\n/gu, '\n').trim()
  const encoded = normalized.includes('BEGIN PUBLIC KEY')
    ? normalized.replace(/-----BEGIN PUBLIC KEY-----|-----END PUBLIC KEY-----|\s/gu, '')
    : normalized.replace(/\s/gu, '')
  const decoded = decodeBase64(encoded)
  return decoded.length === nacl.sign.publicKeyLength ? decoded : decoded.slice(-nacl.sign.publicKeyLength)
}

function decodeBase64(value: string) {
  const binary = atob(value)
  return Uint8Array.from(binary, character => character.charCodeAt(0))
}

function getOrCreateDeviceId() {
  const existing = getStorage(DEVICE_ID_KEY)
  if (existing) {
    return existing
  }

  const generated = crypto.randomUUID()
  setStorage(DEVICE_ID_KEY, generated)
  return generated
}

function compareVersion(left: string, right: string) {
  const a = left.split(/[.-]/u).map(part => Number(part) || 0)
  const b = right.split(/[.-]/u).map(part => Number(part) || 0)
  const length = Math.max(a.length, b.length)
  for (let i = 0; i < length; i += 1) {
    if ((a[i] ?? 0) > (b[i] ?? 0)) {
      return 1
    }
    if ((a[i] ?? 0) < (b[i] ?? 0)) {
      return -1
    }
  }
  return 0
}
